import CakeOutlinedIcon from "@mui/icons-material/CakeOutlined";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import {Avatar, ButtonBase, IconButton, Stack, Tooltip} from "@mui/material";
import {alpha} from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import ProfilePreviewDialog from "crqlar-react-common/dist/components/profilePreview/ProfilePreviewDialog";
import type {AggregationSurveySubmissionListItem}
    from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import type {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import type {Reservation} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {getGuestStayStatusInfo} from "crqlar-react-common/dist/utils/color.util";
import {convertLocalDateToDate, formatFullDMY} from "crqlar-react-common/dist/utils/date.util";
import {birthdayToAge, formatPhoneForDisplay} from "crqlar-react-common/dist/utils/profile.util";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import React, {useEffect, useState} from "react";
import ReservationDialog from "../../reservations/reservationDialog/ReservationDialog";
import {ElementChip} from "../../common/uiElements/base/ElementChip";
import {LanguageElement} from "../../common/uiElements/LanguageElement";
import {StayStatusElement} from "../../common/uiElements/StayStatusElement";
import type {SurveySubmissionGuest} from "./export/useSurveySubmissionGuest";

interface Props {
    submission: AggregationSurveySubmissionListItem
    /** The dialog waits for this lookup before revealing the panel. Exports use the same data. */
    guest: SurveySubmissionGuest
}

const CHIP_ICON_SX = {fontSize: 15, color: 'text.secondary'};

// The primary entry first; hidden ones are the PMS's way of saying "do not use".
const firstVisible = <T extends {hidden?: boolean, isPrimary?: boolean}>(items: T[] | undefined, has: (item: T) => boolean) =>
    [...(items ?? [])]
        .filter(item => has(item) && !item.hidden)
        .sort((a, b) => Number(!!b.isPrimary) - Number(!!a.isPrimary))[0];

// Plain text on purpose: no tel:/mailto:, staff reach guests from their own phone and mail client.
const ContactLine = ({icon, text}: {icon: React.ReactNode, text: string}) => (
    <Stack direction={'row'} alignItems={'center'} gap={1} sx={{minWidth: 0}}>
        {icon}
        <Typography variant={'body1'} sx={{overflowWrap: 'anywhere'}}>
            {text}
        </Typography>
    </Stack>
);

const GuestChips = ({profile}: {profile: Profile}) => {
    const {t, language} = useLocalize();

    const stayStatus = getGuestStayStatusInfo(profile);
    const completedStays = profile.completedStaysNo ?? profile.smartData?.stays?.totalStays ?? profile.allStaysNo ?? 0;
    const birthday = profile.birthDay ? convertLocalDateToDate(profile.birthDay) : undefined;
    const age = profile.birthDay ? birthdayToAge(profile.birthDay) : undefined;
    const hasAge = age !== undefined && Number.isFinite(age) && age >= 0;

    return (
        <Stack direction={'row'} flexWrap={'wrap'} alignItems={'center'} gap={0.75}>
            <StayStatusElement
                stayStatus={stayStatus?.status}
                startDate={stayStatus?.stay?.startDay}
                endDate={stayStatus?.stay?.endDay}
            />

            <ElementChip
                text={`${t('Completed stays')}: ${completedStays}`}
                startIcon={<HistoryRoundedIcon sx={CHIP_ICON_SX}/>}
            />

            {hasAge && (
                <ElementChip
                    text={t('{{count}} years', {count: age})}
                    tooltipContent={birthday ? formatFullDMY(birthday) : undefined}
                    startIcon={<CakeOutlinedIcon sx={CHIP_ICON_SX}/>}
                />
            )}

            <LanguageElement languageCode={profile.languageCode} locale={language}/>
        </Stack>
    );
};

interface ReplyRowProps {
    title: string
    detail?: string
    onOpen: () => void
}

const ReplyRow = ({title, detail, onOpen}: ReplyRowProps) => {
    const {t} = useLocalize();

    return (
        <ButtonBase
            onClick={onOpen}
            aria-label={`${t('Open reservation')}: ${title}`}
            data-dashboard-control
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 1.5,
                py: 1.25,
                textAlign: 'left',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                '&:hover': {bgcolor: 'action.hover'},
            }}
        >
            <Stack gap={0.25} sx={{flex: 1, minWidth: 0}}>
                <Typography variant={'body2'} color={'text.secondary'}>
                    {t('This reply')}
                </Typography>
                <Typography variant={'body1'} fontWeight={600} sx={{lineHeight: 1.35, overflowWrap: 'anywhere'}}>
                    {title}
                </Typography>
                {!!detail && (
                    <Typography variant={'body2'} color={'text.secondary'} sx={{overflowWrap: 'anywhere'}}>
                        {detail}
                    </Typography>
                )}
            </Stack>

            <ChevronRightRoundedIcon sx={{color: 'text.secondary', flexShrink: 0}}/>
        </ButtonBase>
    );
};

/**
 * Who answered and what the reply was about - only what a reviewer needs to decide and follow up.
 * Profile and reservation open in place, on top of the submission dialog.
 */
const SurveySubmissionGuestCard = ({submission, guest}: Props) => {
    const {t} = useLocalize();

    const [shownProfile, setShownProfile] = useState<Profile>();
    const [shownReservation, setShownReservation] = useState<Reservation>();

    // Moving on to the next reply must not carry the previous guest's dialogs along.
    useEffect(() => {
        setShownProfile(undefined);
        setShownReservation(undefined);
    }, [submission.id]);

    const profile = guest.profile;
    const replyRow = guest.contextRows.find(row => row.kind === 'reservation');
    const phone = firstVisible(profile?.phones, item => !!item.phoneNumberFull?.trim());
    const email = firstVisible(profile?.emails, item => !!item.email?.trim());
    return (
        <Stack gap={2}>
            <Stack direction={'row'} alignItems={'flex-start'} gap={1.5} sx={{minWidth: 0}}>
                <Avatar
                    sx={theme => ({
                        width: 48,
                        height: 48,
                        flexShrink: 0,
                        borderRadius: '8px',
                        fontWeight: 600,
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                        color: 'primary.main',
                    })}
                >
                    {guest.initials || <PersonOutlineIcon/>}
                </Avatar>

                <Stack gap={0.75} sx={{flex: 1, minWidth: 0}}>
                    <Typography variant={'h6'} fontWeight={600} sx={{lineHeight: 1.35, overflowWrap: 'anywhere'}}>
                        {guest.name}
                    </Typography>

                    {!!profile && <GuestChips profile={profile}/>}

                    {!replyRow && !!submission.sectorName && (
                        <Typography variant={'body2'} color={'text.secondary'}>
                            {submission.sectorName}
                        </Typography>
                    )}
                </Stack>

                {!!profile && (
                    <Tooltip title={t('Open profile')}>
                        <IconButton
                            onClick={() => setShownProfile(profile)}
                            aria-label={`${t('Open profile')}: ${guest.name}`}
                            data-dashboard-control
                            sx={{width: 40, height: 40, flexShrink: 0}}
                        >
                            <PersonSearchOutlinedIcon fontSize={'small'}/>
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>

            {(!!phone || !!email) && (
                <Stack gap={0.75}>
                    {!!phone && (
                        <ContactLine
                            icon={<PhoneOutlinedIcon sx={{fontSize: 18, color: 'text.secondary'}}/>}
                            text={formatPhoneForDisplay(phone.phoneNumberFull)}
                        />
                    )}
                    {!!email && (
                        <ContactLine
                            icon={<MailOutlineRoundedIcon sx={{fontSize: 18, color: 'text.secondary'}}/>}
                            text={email.email!.trim().toLowerCase()}
                        />
                    )}
                </Stack>
            )}

            {!!replyRow && !!guest.reservation && (
                <ReplyRow
                    title={replyRow.title}
                    detail={replyRow.detail}
                    onOpen={() => setShownReservation(guest.reservation)}
                />
            )}

            <ProfilePreviewDialog
                open={!!shownProfile}
                onClose={() => setShownProfile(undefined)}
                profile={shownProfile}
                setProfile={setShownProfile}
            />

            <ReservationDialog
                open={!!shownReservation}
                reservation={shownReservation}
                onClose={() => setShownReservation(undefined)}
                onSave={() => setShownReservation(undefined)}
                onChangeReservation={setShownReservation}
            />
        </Stack>
    );
};

export default SurveySubmissionGuestCard;
